import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAuthAPI } from "@/hooks/auth/useAuthAPI";

export const useLoginForm = (isAuthenticated) => {
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [step, setStep] = useState(1);
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const { requestCode, verifyCode } = useAuthAPI();
    
    // Redirect path after login (if coming from a protected route)
    const from = location.state?.from || "/";
    
    // Redirect if already logged in
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/knowledge');
        }
    }, [isAuthenticated, navigate]);

    // Validate email format
    useEffect(() => {
        setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    }, [email]);

    const handleEmailChange = (value) => {
        setEmail(value);
    };

    const handleCodeChange = (value) => {
        setCode(value);
    };

    const handleRequestCode = async () => {
        if (!isEmailValid || isLoading) return;
        
        setIsLoading(true);
        setError("");
        
        try {
            const result = await requestCode(email);
            
            if (result.isSuccess) {
                setStep(2);
            } else {
                setError(result.message || "Failed to request login code");
            }
        } catch (error) {
            console.error("Login code request error:", error);
            setError("An error occurred. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        if (code.length < 5 || isLoading) return;
        
        setIsLoading(true);
        setError("");
        
        try {
            const result = await verifyCode(email, code);
            
            if (result.isSuccess) {
                // Use our auth hook for login
                login(result.data.token);
                
                // Redirect to the original page or home
                navigate(from);
            } else {
                setError(result.message || "Failed to verify code");
            }
        } catch (error) {
            console.error("Verification error:", error);
            setError("An error occurred while verifying the code. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = () => {
        handleRequestCode();
    };

    const handleTryAnotherEmail = () => {
        setStep(1);
        setEmail("");
        setCode("");
        setError("");
    };

    // Handle key press events
    const handleKeyPress = (event, action) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            action();
        }
    };

    return {
        email,
        code,
        step,
        isEmailValid,
        isLoading,
        error,
        handleEmailChange,
        handleCodeChange,
        handleRequestCode,
        handleVerifyCode,
        handleResendCode,
        handleTryAnotherEmail,
        handleKeyPress
    };
};